// Gebyrer: weekend/helligdag, flytning med kort varsel (under flytTimer timer før – standard 24), aflysning med kort varsel
// (under 48 og under 24 timer) og faktura i stedet for MobilePay/kort.
// Beløb i øre ekskl. moms (som alle andre priser). Kan rettes under Indstillinger › Gebyrer.
import { helligdage, minutterTil } from './dato.ts';
import { moms } from './pris.ts';

export type GebyrType = 'weekend' | 'helligdag' | 'flyt' | 'aflys' | 'faktura';
export type GebyrIndst = {
  weekend: number;        // lørdag og søndag (0 = slået fra)
  helligdag: number;      // helligdage (0 = slået fra)
  flyt: number;           // flytning under flytTimer timer før (0 = gratis). Kunden betaler altid; kontoret vælger.
  flytTimer: number;      // grænsen for kort varsel ved flytning i timer (standard 24)
  aflys48: number;        // kunden aflyser under 48 timer før (0 = ingen gebyr)
  aflys24: number;        // … under 24 timer før
  faktura: number;        // faktura i stedet for betalingslink
};
export const STANDARD_GEBYRER: GebyrIndst = { weekend: 4900, helligdag: 4900, flyt: 9900, flytTimer: 24, aflys48: 0, aflys24: 0, faktura: 2900 };
/** Beløbene (øre ekskl. moms). flytTimer er en grænse i timer og tjekkes for sig (FLYT_TIMER). */
export const GEBYR_FELTER: Exclude<keyof GebyrIndst, 'flytTimer'>[] = ['weekend', 'helligdag', 'flyt', 'aflys48', 'aflys24', 'faktura'];
export const FLYT_TIMER = { min: 1, max: 168 };

/** Gamle indstillinger med to flyttetrin (flyt48/flyt24) læses som ét gebyr under 24 timer – det højeste af dem, der er sat. */
export function gebyrerFra(g: Partial<GebyrIndst> & { flyt48?: number; flyt24?: number } | undefined | null): GebyrIndst {
  const x = { ...STANDARD_GEBYRER, ...(g ?? {}) } as GebyrIndst & { flyt48?: number; flyt24?: number };
  if (g && (g.flyt48 != null || g.flyt24 != null)) { x.flyt = Number(g.flyt24) || Number(g.flyt48) || 0; x.flytTimer = 24; }
  const ud = {} as GebyrIndst;
  for (const k of GEBYR_FELTER) ud[k] = Number(x[k]) || 0;
  const t = Math.round(Number(x.flytTimer));
  ud.flytTimer = Number.isFinite(t) && t >= FLYT_TIMER.min && t <= FLYT_TIMER.max ? t : STANDARD_GEBYRER.flytTimer;
  return ud;
}

/** Et gebyr på en booking. Rykkerforløbet (core/rykker.ts) lægger rykkergebyrer, kompensation og renter på som momsfri linjer. */
export type Gebyr = { type: GebyrType | 'rykker' | 'kompensation' | 'rente'; navn: string; beloeb: number; moms: number; tid: string };

export const GEBYR_NAVN: Record<GebyrType, string> = {
  weekend: 'Weekendtillæg', helligdag: 'Helligdagstillæg', flyt: 'Gebyr for flytning med kort varsel', aflys: 'Gebyr for aflysning med kort varsel', faktura: 'Faktura- og administrationsgebyr',
};

const helligCache = new Map<number, Set<string>>();
export function erHelligdag(dato: string): boolean {
  const aar = Number(dato.slice(0, 4));
  let s = helligCache.get(aar);
  if (!s) { s = new Set(helligdage(aar).map((h) => h.dato)); helligCache.set(aar, s); }
  return s.has(dato);
}
export function erWeekend(dato: string): boolean {
  const d = new Date(dato + 'T12:00:00Z').getUTCDay();
  return d === 0 || d === 6;
}

/** Tillæg for datoen: helligdag går forud for weekend (aldrig begge). */
export function datoGebyr(dato: string, g: GebyrIndst = STANDARD_GEBYRER): { type: GebyrType; beloeb: number } | null {
  if (erHelligdag(dato) && g.helligdag > 0) return { type: 'helligdag', beloeb: g.helligdag };
  if (erWeekend(dato) && g.weekend > 0) return { type: 'weekend', beloeb: g.weekend };
  return null;
}

/** Gebyrer uden moms: fakturagebyret er momsfritaget (SteamGreen: 29 kr. uanset hvad). */
export const MOMSFRIE_GEBYRER: readonly GebyrType[] = ['faktura'];
export const gebyrMoms = (type: GebyrType, beloeb: number, momsProcent: number) => (MOMSFRIE_GEBYRER.includes(type) ? 0 : moms(beloeb, momsProcent));

export function lavGebyr(type: GebyrType, beloeb: number, momsProcent: number, tid = new Date().toISOString()): Gebyr {
  return { type, navn: GEBYR_NAVN[type], beloeb, moms: gebyrMoms(type, beloeb, momsProcent), tid };
}

/**
 * Gebyr for at flytte eller aflyse med kort varsel. `minutterTil` = minutter fra nu til tiden (ved flytning den tid, der er
 * tættest på – den nuværende eller den nye). Flytning: under flytTimer timer (standard 24). Aflysning: under 24 timer bruges
 * 24-timers-prisen (eller 48-timers, hvis den ikke er sat), ellers under 48 timer 48-timers-prisen.
 */
export function varselGebyr(art: 'flyt' | 'aflys', minutterTil: number, g: GebyrIndst = STANDARD_GEBYRER): { type: GebyrType; beloeb: number; timer: number } | null {
  if (art === 'flyt') return minutterTil < g.flytTimer * 60 && g.flyt > 0 ? { type: 'flyt', beloeb: g.flyt, timer: g.flytTimer } : null;
  const t48 = g.aflys48, t24 = g.aflys24 || t48;
  if (minutterTil < 24 * 60 && t24 > 0) return { type: art, beloeb: t24, timer: 24 };
  if (minutterTil < 48 * 60 && t48 > 0) return { type: art, beloeb: t48, timer: 48 };
  return null;
}

/** Tider, der tæller med ved kort varsel: en booking uden fast tid (venter på dato / mangler tid) har ingen "nuværende" tid. */
export type VarselTid = { dato: string; start: number | null; status?: string };
const harTid = (b: VarselTid) => b.start != null && b.status !== 'afventer_dato' && b.status !== 'mangler_tid';

/**
 * Kontoret flytter en booking: ligger den nuværende eller den nye tid under flytTimer timer væk, skal kontoret vælge med eller
 * uden flytgebyr (og huske at fortælle kunden det). null = ingen dialog (samme tid, kun ny steamer, uden for grænsen, intet gebyr).
 */
export function kontorFlytVarsel(foer: VarselTid, ny: { dato: string; start: number | null }, g: GebyrIndst): { beloeb: number; timer: number } | null {
  if (ny.start == null || (foer.dato === ny.dato && foer.start === ny.start)) return null;
  if (foer.status && !['afventer', 'godkendt'].includes(foer.status)) return null;
  const m = Math.min(harTid(foer) ? minutterTil(foer.dato, foer.start!) : Infinity, minutterTil(ny.dato, ny.start));
  const v = varselGebyr('flyt', m, g);
  return v && { beloeb: v.beloeb, timer: v.timer };
}
